import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContextService } from 'app/core/services/context.service';
import { SitesService } from 'shared/services';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, switchMap } from 'rxjs';
import { SiteLocation } from 'app/shared/types';

@Injectable({
  providedIn: 'root',
})
export class LocationNameMapperService {
  public isReady = new BehaviorSubject(false);
  private contextService = inject(ContextService);
  private destroyRef = inject(DestroyRef);
  private siteService = inject(SitesService);
  private siteLocations: SiteLocation[] = [];
  private readonly refreshTrigger$ = new BehaviorSubject(false);

  constructor() {
    this.watchForSiteId();
  }

  private watchForSiteId(): void {
    combineLatest([this.contextService.siteId, this.refreshTrigger$])
      .pipe(
        distinctUntilChanged(),
        filter(([siteId]) => siteId !== null),
        switchMap(([siteId]) => {
          return this.siteService.getSiteLocations(siteId as string);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((val) => {
        this.siteLocations = val?.value.locations || [];
        this.isReady.next(true);
      });
  }

  public refreshLocations(): void {
    this.isReady.next(false);
    this.refreshTrigger$.next(true);
  }

  public injectLocationName<T>({
    propertyName,
    idPropertyName,
    items,
  }: {
    propertyName: string;
    idPropertyName: string;
    items: T;
  }): T {
    if (Array.isArray(items)) {
      return items.map((itemValue) => {
        const id = itemValue[idPropertyName as keyof T];
        const locationName = this.siteLocations.find((loc) => loc.id === id)?.name;

        return {
          ...itemValue,
          [propertyName]: locationName,
        };
      }) as T;
    } else {
      return {
        ...items,
        [propertyName]: this.siteLocations.find((siteLoc) => siteLoc.id === items[idPropertyName as keyof T])?.name,
      };
    }
  }
}
